import { Link, useLocation } from "react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSidebar } from "../context/SidebarContext";
import { RootState, useAppSelector } from "../redux/store";
import {
  GridIcon,
  ListIcon,
  TableIcon,
  HorizontaLDots,
  ChevronDownIcon,
} from "../icons";

type NavItem = {
  name: string;
  path?: string;
  icon: React.ReactNode;
  subItems?: { name: string; path: string; pro?: boolean; new?: boolean }[];
};

const navItems = ({
  lang,
  role,
  t,
}: {
  lang: string;
  role: string;
  t: (key: string) => string;
}): NavItem[] => {
  return [
    {
      name: t("sidebar.dashboard"),
      icon: <GridIcon />,
      path: `/${lang}/${role}/dashboard`,
    },
    {
      name: t("sidebar.schoolSetup"),
      icon: <ListIcon />,
      path: `/${lang}/${role}/school`,
    },
    {
      name: t("sidebar.examRegistration"),
      icon: <ListIcon />,
      subItems: [
        {
          name: t("sidebar.examRegistrations"),
          path: `/${lang}/${role}/exams`,
        },
        {
          name: t("sidebar.subjectRegistrations"),
          path: `/${lang}/${role}/exams/subjects`,
        },
      ],
    },
    {
      name: t("sidebar.reports"),
      icon: <TableIcon />,
      path: `/${lang}/${role}/reports`,
    },
    {
      name: t("sidebar.payments"),
      icon: <TableIcon />,
      path: `/${lang}/${role}/payments`,
    },
    {
      name: t("sidebar.examMaterials"),
      icon: <ListIcon />,
      subItems: [
        {
          name: t("sidebar.mockRegForms"),
          path: `/${lang}/${role}/exams/registration-forms`,
          pro: false,
        },
        {
          name: t("sidebar.schemesOfWork"),
          path: `/${lang}/${role}/exams/schemes-of-work`,
        },
        {
          name: t("sidebar.pastExamQuestions"),
          path: `/${lang}/${role}/exams/past-exam-questions`,
        },
      ],
    },
  ];
};

const SchoolSidebar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  // Prioritize localStorage on initial load, then use i18n.language for updates
  const storedLang = localStorage.getItem("lang");
  const lang =
    storedLang && (storedLang === "en" || storedLang === "fr")
      ? storedLang
      : i18n.language || "en";
  const { user } = useAppSelector((state: RootState) => state.auth);
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();

  const role = user?.role?.toLowerCase() || "user";

  // Sync i18n language with localStorage on mount
  useEffect(() => {
    const storedLang = localStorage.getItem("lang");
    if (
      storedLang &&
      (storedLang === "en" || storedLang === "fr") &&
      i18n.language !== storedLang
    ) {
      i18n.changeLanguage(storedLang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Memoize navItems to recalculate when language, role, or i18n language changes
  // lang is derived from i18n.language, but we keep both for explicit dependency tracking
  const items = useMemo(() => navItems({ lang, role, t }), [lang, role, t]);

  const [openSubmenu, setOpenSubmenu] = useState<{
    type: "main" | "others";
    index: number;
  } | null>(null);
  const [subMenuHeight, setSubMenuHeight] = useState<Record<string, number>>(
    {}
  );
  const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // const isActive = (path: string) => location.pathname === path;
  const isActive = useCallback(
    (path: string) => location.pathname === path,
    [location.pathname]
  );

  useEffect(() => {
    let submenuMatched = false;

    ["main", "others"].forEach((menuType) => {
      items.forEach((nav, index) => {
        if (nav.subItems) {
          nav.subItems.forEach((subItem) => {
            if (isActive(subItem.path)) {
              setOpenSubmenu({
                type: menuType as "main" | "others",
                index,
              });
              submenuMatched = true;
            }
          });
        }
      });
    });

    if (!submenuMatched) {
      setOpenSubmenu(null);
    }
  }, [location, isActive, items]);

  useEffect(() => {
    if (openSubmenu !== null) {
      const key = `${openSubmenu.type}-${openSubmenu.index}`;
      if (subMenuRefs.current[key]) {
        setSubMenuHeight((prevHeights) => ({
          ...prevHeights,
          [key]: subMenuRefs.current[key]?.scrollHeight || 0,
        }));
      }
    }
  }, [openSubmenu]);

  const handleSubmenuToggle = (index: number, menuType: "main" | "others") => {
    setOpenSubmenu((prevOpenSubmenu) => {
      if (
        prevOpenSubmenu &&
        prevOpenSubmenu.type === menuType &&
        prevOpenSubmenu.index === index
      ) {
        return null;
      }
      return { type: menuType, index };
    });
  };

  const renderMenuItems = (items: NavItem[], menuType: "main" | "others") => (
    <ul className="flex flex-col gap-4">
      {items.map((nav, index) => (
        <li key={nav.name}>
          {nav.subItems ? (
            <button
              onClick={() => handleSubmenuToggle(index, menuType)}
              className={`menu-item group ${
                openSubmenu?.type === menuType && openSubmenu?.index === index
                  ? "menu-item-active"
                  : "menu-item-inactive"
              } cursor-pointer ${
                !isExpanded && !isHovered
                  ? "lg:justify-center"
                  : "lg:justify-start"
              }`}
            >
              <span
                className={`menu-item-icon-size  ${
                  openSubmenu?.type === menuType && openSubmenu?.index === index
                    ? "menu-item-icon-active"
                    : "menu-item-icon-inactive"
                }`}
              >
                {nav.icon}
              </span>
              {(isExpanded || isHovered || isMobileOpen) && (
                <span className="menu-item-text">{nav.name}</span>
              )}
              {(isExpanded || isHovered || isMobileOpen) && (
                <ChevronDownIcon
                  className={`ml-auto w-5 h-5 transition-transform duration-200 ${
                    openSubmenu?.type === menuType &&
                    openSubmenu?.index === index
                      ? "rotate-180 text-brand-500"
                      : ""
                  }`}
                />
              )}
            </button>
          ) : (
            nav.path && (
              <Link
                to={nav.path}
                className={`menu-item group ${
                  isActive(nav.path) ? "menu-item-active" : "menu-item-inactive"
                }`}
              >
                <span
                  className={`menu-item-icon-size ${
                    isActive(nav.path)
                      ? "menu-item-icon-active"
                      : "menu-item-icon-inactive"
                  }`}
                >
                  {nav.icon}
                </span>
                {(isExpanded || isHovered || isMobileOpen) && (
                  <span className="menu-item-text">{nav.name}</span>
                )}
              </Link>
            )
          )}
          {nav.subItems && (isExpanded || isHovered || isMobileOpen) && (
            <div
              ref={(el) => {
                subMenuRefs.current[`${menuType}-${index}`] = el;
              }}
              className="overflow-hidden transition-all duration-300"
              style={{
                height:
                  openSubmenu?.type === menuType && openSubmenu?.index === index
                    ? `${subMenuHeight[`${menuType}-${index}`]}px`
                    : "0px",
              }}
            >
              <ul className="mt-2 space-y-1 ml-9">
                {nav.subItems.map((subItem) => (
                  <li key={subItem.name}>
                    <Link
                      to={subItem.path}
                      className={`menu-dropdown-item ${
                        isActive(subItem.path)
                          ? "menu-dropdown-item-active"
                          : "menu-dropdown-item-inactive"
                      }`}
                    >
                      {subItem.name}
                      <span className="flex items-center gap-1 ml-auto">
                        {subItem.new && (
                          <span
                            className={`ml-auto ${
                              isActive(subItem.path)
                                ? "menu-dropdown-badge-active"
                                : "menu-dropdown-badge-inactive"
                            } menu-dropdown-badge`}
                          >
                            new
                          </span>
                        )}
                        {subItem.pro && (
                          <span
                            className={`ml-auto ${
                              isActive(subItem.path)
                                ? "menu-dropdown-badge-active"
                                : "menu-dropdown-badge-inactive"
                            } menu-dropdown-badge`}
                          >
                            pro
                          </span>
                        )}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </li>
      ))}
    </ul>
  );

  return (
    <aside
      className={`fixed mt-16 flex flex-col lg:mt-0 top-0 px-5 left-0 bg-white dark:bg-gray-900 dark:border-gray-800 text-gray-900 h-screen transition-all duration-300 ease-in-out z-50 border-r border-gray-200 
        ${
          isExpanded || isMobileOpen
            ? "w-[290px]"
            : isHovered
            ? "w-[290px]"
            : "w-[90px]"
        }
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`py-8 flex ${
          !isExpanded && !isHovered ? "lg:justify-center" : "justify-start"
        }`}
      >
        <Link to="/">
          {isExpanded || isHovered || isMobileOpen ? (
            <div className="flex gap-2">
              <img
                src="/images/logo/logo-icon.svg"
                alt="Logo"
                width={32}
                height={32}
              />
              <p className="text-xl font-extrabold dark:text-white">TRU NW</p>
            </div>
          ) : (
            <img
              src="/images/logo/logo-icon.svg"
              alt="Logo"
              width={32}
              height={32}
            />
          )}
        </Link>
      </div>
      <div className="flex flex-col overflow-y-auto duration-300 ease-linear no-scrollbar">
        <nav className="mb-6">
          <div className="flex flex-col gap-4">
            <div>
              <h2
                className={`mb-4 text-xs uppercase flex leading-[20px] text-gray-400 ${
                  !isExpanded && !isHovered
                    ? "lg:justify-center"
                    : "justify-start"
                }`}
              >
                {isExpanded || isHovered || isMobileOpen ? (
                  t("sidebar.menu")
                ) : (
                  <HorizontaLDots className="size-6" />
                )}
              </h2>
              {renderMenuItems(items, "main")}
            </div>
          </div>
        </nav>
      </div>
    </aside>
  );
};

export default SchoolSidebar;
